/**
 * Region-equality guard helper. Pure: takes (requested, current),
 * throws on mismatch. Defense-in-depth alongside the WS-B6 route-resolver
 * Worker — protects against a misrouted client landing a workspace
 * mutation in the wrong region's Postgres.
 *
 * Today (no logical replication), the FK + auth-check layers already
 * catch most cross-region-misroute scenarios. This guard becomes
 * load-bearing post-WS-B7b once `workspaces` rows replicate into both
 * regions while only ONE region is authoritative for writes.
 *
 * Usage pattern (server-only — gate on `tx.location === "server"` so
 * client optimistic mutations skip the check):
 *
 *   if (tx.location === "server") {
 *     assertRegionMatches(args.region, ctx?.currentRegion);
 *   }
 *
 * `currentRegion` flows in via `ctx` from the PushProcessor
 * instantiation in `server/app-zero-sync-routes.ts` —
 * `new PushProcessor(zql, { sub: userId, currentRegion })`.
 *
 * Lives next to its consumers in `mutators/` so callers import it as a
 * sibling. Future mutators that need the same check (workspaces.update,
 * workspaces.upsert, etc.) can import from the same path without
 * tripping the junk-drawer-name lint rule on `**\/utils`.
 */

/**
 * Throw if `requested` and `current` are both set and differ. Both-null
 * / either-null cases are no-ops — the guard is purely "if the request
 * specifies a region that's different from this server's region, refuse."
 *
 * Why permissive on missing values:
 * - `requested === undefined`: legacy / pre-WS-B13a path where the user
 *   hadn't picked a region. Workspace defaults to "us" — that's fine.
 * - `current === undefined`: server didn't pass `currentRegion` through
 *   ctx (older PushProcessor wiring). Don't break those servers; the
 *   guard adds value when wired and is silent when not.
 */
export function assertRegionMatches(
  requested: string | undefined | null,
  current: string | undefined | null,
): void {
  if (!requested || !current) return;
  if (requested === current) return;
  throw new Error(
    `Region mismatch: this server is "${current}", request targets region "${requested}". Route to api-${requested}.ano.dev instead.`,
  );
}
